import type { GardenTemplate } from '@/data/gardenTemplates';
import { createBed, createPlacement } from '@/data/gardenPlanner';
import type { GardenBed, PlantPlacement } from '@/data/gardenPlanner';
import type { Plant } from '@/data/plants';

type Point = { x: number; y: number };
type Circle = Point & { plantId: string; radius: number };

const GRID_SNAP_INCHES = 6;

function parseBedDimensions(size: string): { width: number; height: number } {
  const match = size.match(/(\d+)\s*[xX×]\s*(\d+)/);
  if (match) {
    const a = Number.parseInt(match[1], 10);
    const b = Number.parseInt(match[2], 10);
    return { width: Math.max(a, b), height: Math.min(a, b) };
  }
  if (/window\s*box/i.test(size)) return { width: 6, height: 1 };
  if (/container|pot/i.test(size)) return { width: 4, height: 2 };
  return { width: 8, height: 4 };
}

function parseSpacingInches(spacing: string): number {
  const match = spacing.match(/(\d+)/);
  return match ? Number.parseInt(match[1], 10) : 12;
}

function normalizeName(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function plantHasAnyTerm(plant: Plant | undefined, terms: string[]): boolean {
  if (!plant) return false;
  const haystack = normalizeName([
    plant.id,
    plant.name,
    plant.scientificName,
    plant.type,
    ...(plant.traits ?? []),
  ].join(' '));
  return terms.some(term => haystack.includes(term));
}

function getTemplatePlantRadius(plantId: string, plantLookup: ReadonlyMap<string, Plant>): number {
  const plant = plantLookup.get(plantId);
  if (!plant) return 6;

  const spacing = parseSpacingInches(plant.spacing);
  let scale = 0.5;

  if (plantHasAnyTerm(plant, ['tomato', 'pepper', 'okra', 'corn'])) scale = 0.42;
  if (plantHasAnyTerm(plant, ['basil', 'parsley', 'cilantro', 'thyme', 'oregano', 'chive'])) scale = 0.38;
  if (plantHasAnyTerm(plant, ['carrot', 'radish', 'beet', 'onion', 'garlic', 'lettuce'])) scale = 0.34;
  if (plantHasAnyTerm(plant, ['squash', 'pumpkin', 'melon', 'cucumber', 'zucchini'])) scale = 0.58;

  return Math.min(22, Math.max(4, spacing * scale));
}

function snapToGrid(value: number): number {
  return Math.round(value / GRID_SNAP_INCHES) * GRID_SNAP_INCHES;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function clampToBed(point: Point, radius: number, bedWidth: number, bedHeight: number): Point {
  return {
    x: clamp(snapToGrid(point.x), radius, Math.max(radius, bedWidth - radius)),
    y: clamp(snapToGrid(point.y), radius, Math.max(radius, bedHeight - radius)),
  };
}

function circlesOverlap(left: Circle, right: Circle): boolean {
  return Math.hypot(left.x - right.x, left.y - right.y) < left.radius + right.radius;
}

function resolvePoint(
  plantId: string,
  desired: Point,
  placed: Circle[],
  plantLookup: ReadonlyMap<string, Plant>,
  bedWidth: number,
  bedHeight: number,
): Point | null {
  const radius = getTemplatePlantRadius(plantId, plantLookup);
  const first = clampToBed(desired, radius, bedWidth, bedHeight);
  const firstCircle = { ...first, plantId, radius };

  if (!placed.some(circle => circlesOverlap(firstCircle, circle))) return first;

  const maxSteps = Math.ceil(Math.max(bedWidth, bedHeight) / GRID_SNAP_INCHES);
  const seen = new Set<string>([`${first.x}:${first.y}`]);

  for (let ring = 1; ring <= maxSteps; ring += 1) {
    const candidates: Array<Point & { distance: number }> = [];

    for (let dx = -ring; dx <= ring; dx += 1) {
      for (let dy = -ring; dy <= ring; dy += 1) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== ring) continue;
        const candidate = clampToBed({
          x: first.x + dx * GRID_SNAP_INCHES,
          y: first.y + dy * GRID_SNAP_INCHES,
        }, radius, bedWidth, bedHeight);
        const key = `${candidate.x}:${candidate.y}`;
        if (seen.has(key)) continue;
        seen.add(key);
        candidates.push({
          ...candidate,
          distance: Math.hypot(candidate.x - desired.x, candidate.y - desired.y),
        });
      }
    }

    candidates.sort((left, right) => left.distance - right.distance);
    for (const candidate of candidates) {
      const circle = { x: candidate.x, y: candidate.y, plantId, radius };
      if (!placed.some(existing => circlesOverlap(circle, existing))) {
        return { x: candidate.x, y: candidate.y };
      }
    }
  }

  return null;
}

function linePoints(count: number, start: Point, end: Point): Point[] {
  if (count <= 1) return [{ x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 }];
  return Array.from({ length: count }, (_, index) => {
    const ratio = index / (count - 1);
    return {
      x: start.x + (end.x - start.x) * ratio,
      y: start.y + (end.y - start.y) * ratio,
    };
  });
}

function gridPoints(count: number, bounds: { left: number; top: number; right: number; bottom: number }): Point[] {
  const columns = Math.ceil(Math.sqrt(count));
  const rows = Math.ceil(count / columns);
  const points: Point[] = [];

  for (let index = 0; index < count; index += 1) {
    const column = index % columns;
    const row = Math.floor(index / columns);
    points.push({
      x: columns <= 1 ? (bounds.left + bounds.right) / 2 : bounds.left + ((bounds.right - bounds.left) * column) / (columns - 1),
      y: rows <= 1 ? (bounds.top + bounds.bottom) / 2 : bounds.top + ((bounds.bottom - bounds.top) * row) / (rows - 1),
    });
  }

  return points;
}

function edgePoints(count: number, bedWidth: number, bedHeight: number): Point[] {
  const corners = [
    { x: bedWidth - 8, y: 8 },
    { x: bedWidth - 8, y: bedHeight - 8 },
    { x: 8, y: bedHeight - 8 },
    { x: 8, y: 8 },
  ];
  if (count <= corners.length) return corners.slice(0, count);

  return [
    ...linePoints(Math.ceil(count / 2), { x: 8, y: bedHeight - 8 }, { x: bedWidth - 8, y: bedHeight - 8 }),
    ...linePoints(Math.floor(count / 2), { x: bedWidth - 8, y: 8 }, { x: bedWidth - 8, y: bedHeight - 16 }),
  ].slice(0, count);
}

function templateLayoutPoints(
  templateId: string,
  plantId: string,
  quantity: number,
  bedWidth: number,
  bedHeight: number,
): Point[] {
  if (templateId === 'salad-garden') {
    if (plantId === 'lettuce') return linePoints(quantity, { x: 8, y: 10 }, { x: bedWidth - 8, y: 10 });
    if (plantId === 'spinach') return linePoints(quantity, { x: 10, y: 22 }, { x: bedWidth - 10, y: 22 });
    if (plantId === 'arugula') return linePoints(quantity, { x: 14, y: 34 }, { x: bedWidth - 14, y: 34 });
    if (plantId === 'radish') return linePoints(quantity, { x: 5, y: bedHeight - 5 }, { x: bedWidth - 5, y: bedHeight - 5 });
    if (plantId === 'chives') return edgePoints(quantity, bedWidth, bedHeight);
  }

  if (templateId === 'pizza-garden') {
    if (plantId === 'tomato') return linePoints(quantity, { x: 24, y: 16 }, { x: 54, y: 16 });
    if (plantId === 'basil') return linePoints(quantity, { x: 18, y: 31 }, { x: 54, y: 31 });
    if (plantId === 'garlic') return linePoints(quantity, { x: 9, y: bedHeight - 7 }, { x: bedWidth - 9, y: bedHeight - 7 });
    if (plantId === 'oregano') return linePoints(quantity, { x: bedWidth - 16, y: 14 }, { x: bedWidth - 16, y: 14 });
    if (plantId === 'parsley') return linePoints(quantity, { x: bedWidth - 16, y: 32 }, { x: bedWidth - 16, y: 32 });
  }

  if (templateId === 'tea-garden') {
    if (plantId === 'mint') return [{ x: 10, y: bedHeight - 10 }];
    if (plantId === 'lemongrass') return [{ x: bedWidth - 12, y: bedHeight - 12 }];
    if (plantId === 'lavender') return linePoints(quantity, { x: 12, y: 12 }, { x: bedWidth - 12, y: 12 });
    if (plantId === 'chamomile') return linePoints(quantity, { x: 13, y: bedHeight - 12 }, { x: bedWidth - 13, y: bedHeight - 12 });
    if (plantId === 'echinacea') return [{ x: bedWidth / 2, y: bedHeight / 2 }];
  }

  if (templateId === 'beginner-kitchen-garden') {
    if (plantId === 'zucchini') return [{ x: bedWidth - 18, y: bedHeight / 2 }];
    if (plantId === 'tomato') return linePoints(quantity, { x: 22, y: 16 }, { x: 50, y: 16 });
    if (plantId === 'green-beans') return linePoints(quantity, { x: 10, y: bedHeight - 7 }, { x: 58, y: bedHeight - 7 });
    if (plantId === 'basil') return linePoints(quantity, { x: 22, y: 31 }, { x: 50, y: 31 });
    if (plantId === 'marigold') return edgePoints(quantity, bedWidth, bedHeight);
  }

  if (plantId === 'marigold' || plantId === 'calendula') {
    return edgePoints(quantity, bedWidth, bedHeight);
  }

  const plantIsLarge = ['tomato', 'zucchini', 'squash', 'pumpkin', 'melon', 'sunflower'].some(term => plantId.includes(term));
  if (plantIsLarge) {
    return linePoints(quantity, { x: 18, y: 16 }, { x: bedWidth - 18, y: 16 });
  }

  return gridPoints(quantity, {
    left: 10,
    top: Math.min(20, bedHeight / 2),
    right: bedWidth - 10,
    bottom: bedHeight - 10,
  });
}

export function createBedFromTemplate(
  template: GardenTemplate,
  plantLookup: ReadonlyMap<string, Plant>,
): GardenBed {
  const { width, height } = parseBedDimensions(template.recommendedSize);

  const bedType: GardenBed['type'] =
    template.category === 'in-ground' ? 'in-ground'
      : template.category === 'raised-bed' ? 'raised-bed'
        : 'container';

  const bed = createBed(template.name, width, height, bedType);
  const bedWidth = width * 12;
  const bedHeight = height * 12;
  const placements: PlantPlacement[] = [];
  const placedCircles: Circle[] = [];

  for (const templatePlant of template.plants) {
    const desiredPoints = templateLayoutPoints(
      template.id,
      templatePlant.plantId,
      templatePlant.quantity,
      bedWidth,
      bedHeight,
    );

    for (const desired of desiredPoints) {
      const resolved = resolvePoint(
        templatePlant.plantId,
        desired,
        placedCircles,
        plantLookup,
        bedWidth,
        bedHeight,
      );
      if (!resolved) continue;

      placements.push(createPlacement(templatePlant.plantId, resolved.x, resolved.y));
      placedCircles.push({
        ...resolved,
        plantId: templatePlant.plantId,
        radius: getTemplatePlantRadius(templatePlant.plantId, plantLookup),
      });
    }
  }

  return { ...bed, plants: placements };
}
